import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Worker must be set in the same module where Document/Page are used
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Enable WASM and other decoders so embedded images (e.g. JPEG 2000) render
const pdfOptions = {
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

function PdfViewer({ file, title, maxWidth = 1100 }) {
  const [numPages, setNumPages] = useState(null);
  const [pagesToShow, setPagesToShow] = useState(1);

  const pageWidth = () => Math.min(maxWidth, typeof window !== 'undefined' ? window.innerWidth - 48 : maxWidth);

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
    setPagesToShow(1);
  }

  function onPageRenderSuccess(pageNumber) {
    setPagesToShow((prev) => {
      if (pageNumber === prev && prev < numPages) return prev + 1;
      return prev;
    });
  }

  const documentLoading = (
    <div
      style={{
        background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
        border: '2px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '12px',
        padding: '3rem',
        textAlign: 'center',
        color: '#e0e0e0',
        minHeight: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: '3px solid rgba(0, 212, 255, 0.3)',
          borderTopColor: '#00d4ff',
          borderRadius: '50%',
          animation: 'pdfViewerSpin 0.8s linear infinite',
        }}
      />
      <p style={{ margin: 0, fontSize: '1.1rem' }}>Loading PDF…</p>
    </div>
  );

  const pageLoading = (pageNum, total) => (
    <div
      style={{
        width: pageWidth(),
        minHeight: 400,
        background: '#23243a',
        border: '1px solid rgba(0, 212, 255, 0.15)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#00d4ff',
        fontSize: '1rem',
      }}
    >
      Loading page {pageNum} of {total}…
    </div>
  );

  return (
    <section>
      {title && (
        <h2 style={{ color: '#00d4ff', marginBottom: '1rem' }}>{title}</h2>
      )}
      <style>{`
        @keyframes pdfViewerSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Document
        file={file}
        options={pdfOptions}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={documentLoading}
        error={
          <p style={{ color: '#e88', padding: '2rem' }}>Failed to load PDF.</p>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          {numPages &&
            Array.from(new Array(Math.min(pagesToShow, numPages)), (_, i) => {
              const pageNum = i + 1;
              return (
                <div
                  key={pageNum}
                  style={{
                    boxShadow: '0 4px 16px rgba(0, 212, 255, 0.12)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#fff',
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    width={pageWidth()}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    loading={pageLoading(pageNum, numPages)}
                    onRenderSuccess={() => onPageRenderSuccess(pageNum)}
                  />
                </div>
              );
            })}
          {numPages && pagesToShow < numPages && (
            <div
              style={{
                width: pageWidth(),
                minHeight: 120,
                background: '#23243a',
                border: '1px solid rgba(0, 212, 255, 0.15)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00d4ff',
                fontSize: '1rem',
              }}
            >
              Loading next page…
            </div>
          )}
        </div>
      </Document>
    </section>
  );
}

export default PdfViewer;
